import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  makeStyles,
  Skeleton,
  SkeletonItem,
  Spinner,
  type TableColumnDefinition,
  tokens,
} from "@fluentui/react-components"
import type { Page } from "@rema/shared"
import { useInfiniteQuery } from "@tanstack/react-query"
import type { ReactElement, ReactNode } from "react"
import { es } from "../i18n/es"
import { isApiError } from "../lib/api"
import { problemMessage } from "../lib/problem"

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  skeleton: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    paddingTop: tokens.spacingVerticalS,
  },
  clickableRow: {
    cursor: "pointer",
  },
  // Fluent reparte el ancho de columna por flex, pero las celdas no encogen por
  // debajo de su contenido (min-width:auto): un ID/URL largo sin espacios desborda
  // y se solapa con la columna siguiente. min-width:0 habilita el reparto y
  // overflow-wrap parte las cadenas largas en varias líneas en vez de desbordar.
  grid: {
    "& [role=row]": {
      minWidth: 0,
    },
    "& [role=gridcell], & [role=columnheader]": {
      minWidth: 0,
      overflowWrap: "anywhere",
    },
  },
})

const SKELETON_ROWS = 6

export interface DataGridPageProps<TItem> {
  // Clave de cache TanStack (incluir los filtros activos)
  queryKey: readonly unknown[]
  // Página por cursor — docs/04 §1: { data, next_cursor }
  fetchPage: (cursor: string | null) => Promise<Page<TItem>>
  columns: TableColumnDefinition<TItem>[]
  getRowId: (item: TItem) => string
  // Estado vacío consistente (típicamente <EmptyState />)
  emptyState: ReactElement
  // Command bar / filtros encima de la tabla
  toolbar?: ReactNode
  sortable?: boolean
  onRowClick?: (item: TItem) => void
}

// Listado estándar de la consola: DataGrid Fluent + paginación por cursor con
// TanStack Query y estados de carga (Skeleton), error y vacío consistentes.
export const DataGridPage = <TItem,>({
  queryKey,
  fetchPage,
  columns,
  getRowId,
  emptyState,
  toolbar,
  sortable = false,
  onRowClick,
}: DataGridPageProps<TItem>): ReactElement => {
  const styles = useStyles()
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  })

  let body: ReactNode
  if (query.isPending) {
    body = (
      <Skeleton className={styles.skeleton} aria-label={es.common.loading}>
        {Array.from({ length: SKELETON_ROWS }, (_, row) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: filas de skeleton estáticas
          <SkeletonItem key={row} size={32} />
        ))}
      </Skeleton>
    )
  } else if (query.isError) {
    body = (
      <MessageBar intent="error">
        <MessageBarBody>
          {isApiError(query.error) ? problemMessage(query.error.problem) : es.common.loadError}
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="transparent" onClick={() => query.refetch()}>
            {es.common.retry}
          </Button>
        </MessageBarActions>
      </MessageBar>
    )
  } else {
    const items = query.data.pages.flatMap((page) => page.data)
    if (items.length === 0) {
      body = emptyState
    } else {
      body = (
        <>
          <DataGrid
            className={styles.grid}
            items={items}
            columns={columns}
            sortable={sortable}
            getRowId={getRowId}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<TItem>>
              {({ item, rowId }) => (
                <DataGridRow<TItem>
                  key={rowId}
                  className={onRowClick ? styles.clickableRow : undefined}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                >
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
          {query.hasNextPage ? (
            <div className={styles.footer}>
              <Button
                appearance="secondary"
                disabled={query.isFetchingNextPage}
                icon={query.isFetchingNextPage ? <Spinner size="tiny" /> : undefined}
                onClick={() => query.fetchNextPage()}
              >
                {es.common.loadMore}
              </Button>
            </div>
          ) : null}
        </>
      )
    }
  }

  return (
    <div className={styles.root}>
      {toolbar}
      {body}
    </div>
  )
}
